import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Avatar,
  MenuItem,
  Radio,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CapitalizeFirstLetter from "../../../../../src/shared/utils/CapitalizeFirstLetter";
import axios from "axios";

const ReferenceAssignmentModal = ({
  ipData,
  getIPList,
  initialSourceUser,
  users,
  onChange,
  userRole,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Set initial user from props
  useEffect(() => {
    if (initialSourceUser && users.length > 0) {
      const assignedUser = users.find(
        (user) => user._id === initialSourceUser?._id,
      );
      setSelectedUser(assignedUser || null);
    } else {
      setSelectedUser(null);
    }
  }, [initialSourceUser, users]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleUserChange = async (event, newValue) => {
    if (!ipData?._id) return;

    setUpdating(true);
    const previousUser = selectedUser;

    try {
      setSelectedUser(newValue);

      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/installationPartner/edit/${ipData._id}`,
        {
          sourceUser: newValue?._id || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        const successMessage = newValue
          ? `Source assigned to ${CapitalizeFirstLetter(newValue.name)} successfully`
          : "Source removed successfully";

        showSnackbar(successMessage, "success");
        if (onChange) onChange(newValue);
        if (getIPList) getIPList();
      } else {
        throw new Error(response?.data?.error || "Failed to update source");
      }
    } catch (err) {
      console.error("Error updating source:", err);
      setSelectedUser(previousUser);

      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to update source. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setUpdating(false);
    }
  };

  // If there are no users, show a simple add button
  if (users.length === 0) {
    return (
      <div className="flex items-center justify-center">
        <Tooltip title="No users available for assignment">
          <PersonAddAlt1Icon fontSize="small" color="disabled" />
        </Tooltip>
      </div>
    );
  }

  return (
    <>
      <Box
        sx={{
          minWidth: 200,
          display: "flex",
          alignItems: "center",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <Autocomplete
          size="small"
          options={users}
          getOptionLabel={(option) =>
            CapitalizeFirstLetter(option.name) || "Unnamed User"
          }
          value={selectedUser}
          onChange={handleUserChange}
          loading={updating}
          disabled={updating || userRole != -1} // Only allow admins to change assignment
          sx={{
            width: "100%",
            maxWidth: 250,
          }}
          slotProps={{
            paper: {
              sx: {
                width: 250, // 👈 dropdown width
              },
            },
          }}
          clearIcon={null}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedUser ? "Change source" : "Assign source"}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  appearance: "none",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.5rem",
                  paddingRight: "2.5rem",
                  backgroundColor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: selectedUser ? "#93c5fd" : "transparent",
                  },
                  "&.Mui-focused": {
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e5e7eb",
                    cursor: "not-allowed",
                  },
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  padding: "0.625rem 0.75rem",
                  color: selectedUser ? "#374151" : "#6b7280",
                  textAlign: "center",
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: !selectedUser ? (
                  <PersonAddAlt1Icon
                    sx={{
                      mr: 1,
                      color: "#6b7280",
                      fontSize: "1rem",
                    }}
                  />
                ) : null,
                endAdornment: (
                  <>
                    {updating && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const isSelected = selectedUser?._id === option._id;
            return (
              <MenuItem
                {...props}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  "&:hover": {
                    backgroundColor: isSelected ? "#E3F2FD" : "#f5f5f5",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 1,
                    bgcolor: isSelected ? "primary.main" : "grey.300",
                    fontSize: 12,
                    fontWeight: "bold",
                  }}
                >
                  {option.name?.[0]?.toUpperCase() || "U"}
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <div className="flex flex-col text-left font-medium text-[0.9rem]">
                    {CapitalizeFirstLetter(option.name) || "Unnamed User"}
                    <span className="text-[12px] text-gray-500">
                      {option.email}
                    </span>
                  </div>
                </Box>

                <Radio
                  checked={isSelected}
                  size="small"
                  sx={{
                    ml: "auto",
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                />
              </MenuItem>
            );
          }}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          clearOnBlur={false}
          clearOnEscape={false}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReferenceAssignmentModal;
